import { flash, Modal, SubmitButton } from "@xola/ui-kit";
import { useMemoizedFn } from "ahooks";
import React, { useRef, useState } from "react";
import { useMutation } from "react-query";
import { Except } from "type-fest";
import { Experience, ExperienceOption } from "types";
import { api } from "@/api/api";
import { NotifyCustomerCheckbox } from "@/components/Common/NotifyCustomerCheckbox";
import { OptionCard } from "@/components/Common/OptionCard";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { useInvalidate } from "@/hooks/useInvalidation";

export enum DeleteExperienceOptionMode {
    KEEP_ON_FUTURE_EVENTS = "keep_on_future_events",
    REMOVE_FROM_FUTURE_EVENTS = "remove_from_future_events",
}

interface DeleteExperienceOptionModalProps {
    experience: Experience;
    option?: ExperienceOption;
    isOpen: boolean;
    onClose: () => void;
}

export const DeleteExperienceOptionModal = ({
    experience,
    option,
    isOpen,
    onClose,
}: DeleteExperienceOptionModalProps) => {
    const isDeletingRef = useRef(false);

    const handleClose = () => {
        if (isDeletingRef.current) {
            return;
        }
        onClose();
    };

    return (
        <Modal shouldCloseOnOutsideClick={false} isOpen={isOpen} onClose={handleClose}>
            {option ? (
                <ModalContent experience={experience} option={option} isDeletingRef={isDeletingRef} onClose={onClose} />
            ) : null}
        </Modal>
    );
};

const ModalContent = ({
    experience,
    option,
    onClose,
    isDeletingRef,
}: Except<DeleteExperienceOptionModalProps, "isOpen" | "option"> & {
    option: ExperienceOption;
    isDeletingRef: React.MutableRefObject<boolean>;
}) => {
    const invalidate = useInvalidate();
    const [mode, setMode] = useState<DeleteExperienceOptionMode>(DeleteExperienceOptionMode.KEEP_ON_FUTURE_EVENTS);
    const [shouldNotifyCustomer, setShouldNotifyCustomer] = useState(true);

    const handleKeepMode = useMemoizedFn(() => setMode(DeleteExperienceOptionMode.KEEP_ON_FUTURE_EVENTS));
    const handleRemoveMode = useMemoizedFn(() => setMode(DeleteExperienceOptionMode.REMOVE_FROM_FUTURE_EVENTS));
    const handleNotifyChange = useMemoizedFn((event: React.ChangeEvent<HTMLInputElement>) =>
        setShouldNotifyCustomer(event.target.checked),
    );

    const { mutateAsync: deleteOption, isLoading } = useMutation(async (deleteMode: DeleteExperienceOptionMode) => {
        await api.delete(`/experiences/${experience.id}/options/${option.id}`, {
            data: { mode: deleteMode, _emailOverrides: { sendEmailToTraveler: shouldNotifyCustomer } },
        });
    });

    const handleDelete = async () => {
        isDeletingRef.current = true;
        try {
            await deleteOption(mode);
            invalidate("experiences", experience.id);
            invalidate("events");
            invalidate("timeslots");
            flash.success("Experience Option removed");
            onClose();
        } finally {
            isDeletingRef.current = false;
        }
    };

    return (
        <>
            <Modal.Header>
                Delete <var data-var="optionName">{option.name}</var>?
            </Modal.Header>
            <Modal.Body className="space-y-4 text-gray-darker">
                <p>
                    Deleting this option will remove it from all schedules, preventing it from being offered on future
                    events. However, some future events may already have active reservations for this option.
                </p>
                <p className="font-bold">How would you like to handle future events with reservations?</p>
                <OptionCard
                    isSelected={mode === DeleteExperienceOptionMode.KEEP_ON_FUTURE_EVENTS}
                    testId="experience-option-delete-mode-keep"
                    title="Keep on reserved future events"
                    description="Retain on events with existing reservations so you can fulfill those bookings. It will not be available for any new events."
                    onClick={handleKeepMode}
                />
                <OptionCard
                    isSelected={mode === DeleteExperienceOptionMode.REMOVE_FROM_FUTURE_EVENTS}
                    testId="experience-option-delete-mode-remove"
                    title="Remove from all future events"
                    description="Remove from all future events immediately, including those with active reservations."
                    onClick={handleRemoveMode}
                />
            </Modal.Body>
            <Modal.Footer>
                <div className="flex items-center justify-between">
                    <NotifyCustomerCheckbox isChecked={shouldNotifyCustomer} onChange={handleNotifyChange} />
                    <div className="flex items-center space-x-4">
                        <SecondaryButton
                            data-testid="experience-option-delete-cancel-button"
                            disabled={isLoading}
                            onClick={onClose}
                        >
                            Cancel
                        </SecondaryButton>
                        <SubmitButton
                            color="danger"
                            data-testid="experience-option-delete-confirm-button"
                            isLoading={isLoading}
                            onClick={handleDelete}
                        >
                            Delete
                        </SubmitButton>
                    </div>
                </div>
            </Modal.Footer>
        </>
    );
};
